import { MediaImage } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { WhereaboutsModal } from "shared/components/root";
import {
  MAX_FILE_SIZE_IMAGE,
  MAX_FILE_SIZE_VIDEO,
  useUploadMediaModal,
} from "./useUploadMediaModal";
import { FileUpload, ProgressBar } from "@ttc3k/trekker";
import { AssetFile, AssetImage } from "gql/generated";
import { Flex } from "styled-system/jsx";
import { Collapsible } from "shared/components/molecules";

type UploadMediaModalProps = {
  accept?: string;
  isOpen: boolean;
  isPublicWidget: boolean;
  onClose: () => void;
  onUploadComplete: (assets: Partial<AssetFile | AssetImage>[]) => void;
  maxFiles?: number;
  defaultCaption?: string;
  isMaxZIndex?: boolean;
};

export const UploadMediaModal = ({
  accept = "image/*",
  isOpen,
  isPublicWidget = false,
  onClose,
  onUploadComplete,
  maxFiles = Infinity,
  defaultCaption = "",
  isMaxZIndex,
}: UploadMediaModalProps) => {
  const { t } = useTranslation();

  const maxFileSize = accept.includes("video")
    ? MAX_FILE_SIZE_VIDEO
    : MAX_FILE_SIZE_IMAGE;

  const {
    filesToUpload,
    isUploading,
    uploadProgress,
    uploadingFileName,
    handleCloseClick,
    handleFileChange,
    handleFileSelectionError,
    handleUploadFiles,
  } = useUploadMediaModal({
    accept,
    maxFileSize,
    defaultCaption,
    maxFiles,
    isPublicWidget,
    onClose,
    onUploadComplete,
  });

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      title={t("core:IMAGE.UPLOAD.TITLE")}
      HeaderIcon={MediaImage}
      hideCloseTrigger={isUploading}
      onClose={handleCloseClick}
      cancelButtonProps={{
        onClick: handleCloseClick,
        disabled: isUploading,
      }}
      saveButtonCopy={t("core:IMAGE.UPLOAD.CTA_BUTTON")}
      saveButtonProps={{
        visual: "accent.blue",
        onClick: handleUploadFiles,
        isLoading: isUploading,
        disabled: isUploading || filesToUpload.length === 0,
      }}
      size="lg"
      portal={false}
      isMaxZIndex={isMaxZIndex}
    >
      <Flex flexDir={"column"} gap="100">
        <FileUpload
          buttonLabel={t("core:IMAGE.UPLOAD.BROWSE_FILES")}
          label={t("core:IMAGE.UPLOAD.DRAG_HERE")}
          maxFiles={maxFiles}
          maxFilesCopy={
            maxFiles < Infinity && t("core:IMAGE.UPLOAD.MSG_FILE_MAX")
          }
          maxFileSizeCopy={
            accept.includes("video")
              ? t("core:IMAGE.UPLOAD.MSG_FILE_SIZE_WITH_VIDEO")
              : t("core:IMAGE.UPLOAD.MSG_FILE_SIZE")
          }
          maxFileSize={maxFileSize}
          visual="row"
          onFileAccept={handleFileChange}
          borderStyle="dashed"
          accept={accept}
          // This overrides Ark's internal file validation because when uploading images and videos,
          // Ark needs to know the MAX file size, which is too large for images ...
          // Ark will accept the file and then `useUploadMediaModal` will filter out images that are too large
          // We pass the validated file list back to the Ark component here
          acceptedFiles={filesToUpload}
          onFileSelectionError={handleFileSelectionError}
        />
        <Collapsible open={isUploading}>
          <ProgressBar
            label={t("core:IMAGE.UPLOAD.UPLOADING", {
              fileName: uploadingFileName,
            })}
            value={uploadProgress}
            showProgressValueText={true}
            className={{
              marginBottom: "-20px",
            }}
          />
        </Collapsible>
      </Flex>
    </WhereaboutsModal>
  );
};
